import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  PayOverTimeAutopayResultTypenames,
  SubmitPayOverTimeLoanDisbursementResultTypenames,
  SubmitPayOverTimeLoanDisbursementSuccess,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import {
  AutopayConfirmationError,
  logPayOverTimeError,
  type PayOverTimeErrorConfig,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import usePayOverTimeAgreementDocument from 'features/payOverTime/hooks/usePayOverTimeAgreementDocument';
import { useSetAutopayForPayOverTime } from 'features/payOverTime/hooks/useSetAutopayForPayOverTime';
import useSubmitPayOverTimeLoanDisbursement from 'features/payOverTime/hooks/useSubmitPayOverTimeLoanDisbursement';
import PaymentPlanWhatIsAutopayBottomSheet from 'features/payOverTime/screens/application/PaymentPlan/PaymentPlanWhatIsAutopayBottomSheet';
import {
  agreementIdAtom,
  paymentPlanDetailsAtom,
  registrationInfoIdAtom,
  transactionIdAtom,
} from 'features/payOverTime/store/payOverTimeOnboardingStore';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PaymentPlanAgreement from 'shared/components/lending/paymentPlanReview/PaymentPlanAgreement/PaymentPlanAgreement';
import PaymentPlanAutopay from 'shared/components/lending/paymentPlanReview/PaymentPlanAutopay/PaymentPlanAutopay';
import PaymentPlanHeader from 'shared/components/lending/paymentPlanReview/PaymentPlanHeader/PaymentPlanHeader';
import PaymentPlanLineItems from 'shared/components/lending/paymentPlanReview/PaymentPlanLineItems/PaymentPlanLineItems';
import PaymentPlanReviewLayout from 'shared/components/lending/paymentPlanReview/PaymentPlanReviewLayout/PaymentPlanReviewLayout';
import PaymentScheduleTimeline from 'shared/components/lending/PaymentScheduleTimeline/PaymentScheduleTimeline';
import Button from 'shared/components/vxl/Button/Button';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  useViewDidFocusScreenAnalyticsEvent,
  useViewDidFocusTrackAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import usePaymentPlanAccount from 'shared/hooks/usePaymentPlanAccount/usePaymentPlanAccount';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { ErrorSeverity } from 'shared/utils/sentry/sentry.constants';
import { formatDate } from 'shared/utils/string/date';

const PaymentPlan = () => {
  const router = useRouter();
  const [isAutoPayEnabled, setIsAutoPayEnabled] = useState(true);
  const [isAgreed, setIsAgreed] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const errorAnalyticsLoggedRef = useRef(false);
  const whatIsAutopayBottomSheetRef = useRef<BottomSheetModal>(null);
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'paymentPlan',
  });
  const dismissToTab = useDismissToRootTab();
  const { analyticsEvents } = useAnalyticsConfiguration();
  const { transactionId: routeTransactionId } = useLocalSearchParams<{
    transactionId?: string;
  }>();
  const storedTransactionId = useAtomValue(transactionIdAtom);
  const transactionId = routeTransactionId ?? storedTransactionId;
  const paymentPlanDetails = useAtomValue(paymentPlanDetailsAtom);
  const registrationInfoId = useAtomValue(registrationInfoIdAtom);
  const agreementId = useAtomValue(agreementIdAtom);
  const { account: paymentAccount, fetching: isAccountLoading } =
    usePaymentPlanAccount();
  const {
    submitLoanDisbursement,
    fetching: isSubmitting,
    errorModel: disbursementErrorModel,
  } = useSubmitPayOverTimeLoanDisbursement();
  const { setAutopay, fetching: isSettingAutopay } =
    useSetAutopayForPayOverTime();
  const { viewTruthInLendingDocument } = usePayOverTimeAgreementDocument();
  const isSubmittingPlan = isSubmitting || isSettingAutopay;
  const isMissingPlanState =
    !paymentPlanDetails ||
    !transactionId ||
    !registrationInfoId ||
    !agreementId;
  const hasDisbursementError =
    Boolean(disbursementErrorModel) || hasSubmitError;
  const hasErrorState = isMissingPlanState || hasDisbursementError;
  const errorName = isMissingPlanState ? 'data_validation' : 'server_issue';
  useViewDidFocusScreenAnalyticsEvent(
    hasErrorState ? null : { key: '/pay-over-time/payment-plan' },
  );

  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  useBackHandler(goBack);

  const handleWhatIsAutopayPress = () => {
    whatIsAutopayBottomSheetRef.current?.present();
  };

  useEffect(() => {
    if (!hasErrorState) {
      errorAnalyticsLoggedRef.current = false;
      return;
    }

    if (errorAnalyticsLoggedRef.current) {
      return;
    }

    errorAnalyticsLoggedRef.current = true;

    void analyticsEvents.logScreenEventFromConfig(
      'payOverTimeApplicationFlowErrorEncountered',
      { error_name: errorName },
    );
  }, [analyticsEvents, errorName, hasErrorState]);

  const errorTrackAnalytics = hasErrorState
    ? {
        name: 'Pay Over Time Application Flow Error Encountered',
        customAttributes: {
          category: 'pay_over_time',
          source_name: 'pay_over_time_application_error_encountered',
          error_name: errorName,
        },
      }
    : null;
  useViewDidFocusTrackAnalyticsEvent(errorTrackAnalytics);

  const completeDisbursement = async (
    result: SubmitPayOverTimeLoanDisbursementSuccess,
  ) => {
    if (!paymentPlanDetails) {
      return;
    }

    const loanId = result.disbursementId;
    // Not redundant on the ON path. bnpl-service sets autoPay=true in runCatching
    // blocks that log and swallow failures, and disbursement still returns success.
    // Readers resolve the column as `loan.autoPay ?: false`, and the disbursement
    // result exposes only disbursementId, so a silently-failed server write would
    // present as OFF to a customer who was shown ON. Remove only once the
    // disbursement response reports autopay state.
    const autopayResult = await setAutopay({
      loanId,
      enabled: isAutoPayEnabled,
    });

    const isAutopayConfirmed =
      autopayResult?.__typename ===
        PayOverTimeAutopayResultTypenames.PayOverTimeAutopaySuccess &&
      autopayResult.autoPayEnabled === isAutoPayEnabled;

    const unconfirmedError = isAutoPayEnabled
      ? AutopayConfirmationError.UnconfirmedOn
      : AutopayConfirmationError.UnconfirmedOff;
    const autopayConfirmationError = isAutopayConfirmed
      ? undefined
      : unconfirmedError;

    if (autopayConfirmationError) {
      logPayOverTimeError(
        new Error(
          'Pay Over Time autopay could not be confirmed after disbursement',
        ),
        {
          transactionId,
          loanId,
          requestedAutopayEnabled: isAutoPayEnabled,
        },
        ErrorSeverity.CRITICAL,
      );
    }

    const transactionAmount = paymentPlanDetails.lineItems[0]?.value ?? '';
    const firstPayment = paymentPlanDetails.paymentSchedule.scheduleItems[0];

    router.navigate({
      pathname: '/pay-over-time/application-success',
      params: {
        amount: transactionAmount,
        firstPaymentAmount: firstPayment?.amount.formatted ?? '',
        firstPaymentDate: firstPayment
          ? formatDate(firstPayment.dueDate, 'MMMM d')
          : '',
        ...(autopayConfirmationError
          ? { autopayError: autopayConfirmationError, loanId }
          : undefined),
      },
    });
  };

  const handleAcceptPlan = async () => {
    if (!paymentPlanDetails) {
      return;
    }

    const result = await submitLoanDisbursement({
      transactionId,
      registrationInfoId,
      agreementId,
    });

    if (!result) {
      return;
    }

    switch (result.__typename) {
      case SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementSuccess:
        await completeDisbursement(result);
        break;
      case SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementError:
        setHasSubmitError(true);
        break;
      default:
        break;
    }
  };

  const dismissErrorToHome = () => dismissToTab(Tabs.HOME);

  const resolveErrorConfig = (): PayOverTimeErrorConfig => {
    const doneButton = {
      title: translatedText('error.button'),
      onPress: dismissErrorToHome,
    };

    if (isMissingPlanState) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: translatedText('error.title'),
        messageBody: translatedText('error.body'),
        primaryButton: doneButton,
      };
    }

    return {
      screenType: disbursementErrorModel?.screenType ?? ErrorScreenType.Error,
      messageHeadline:
        disbursementErrorModel?.messageHeadline ??
        translatedText('error.title'),
      messageBody:
        disbursementErrorModel?.messageBody ?? translatedText('error.body'),
      primaryButton: doneButton,
    };
  };

  if (hasErrorState) {
    const errorConfig = resolveErrorConfig();

    return (
      <View style={styles.screen}>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ErrorScreen
          screenType={errorConfig.screenType}
          messageHeadline={errorConfig.messageHeadline}
          messageBody={errorConfig.messageBody}
          primaryButton={errorConfig.primaryButton}
        />
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goBack} />
          ),
        }}
      />
      <PaymentPlanReviewLayout
        footer={
          <>
            <Button
              label={translatedText('acceptPlan')}
              analyticsId="paymentPlanAcceptClicked"
              onPress={handleAcceptPlan}
              type="PRIMARY"
              width="FULL"
              isDisabled={!isAgreed || isSubmittingPlan}
            />
            <Button
              label={translatedText('cancel')}
              analyticsId="paymentPlanCancelClicked"
              onPress={goBack}
              type="SECONDARY"
              width="FULL"
            />
          </>
        }
      >
        <PaymentPlanHeader
          title={translatedText('title')}
          summary={paymentPlanDetails.summaryHeadline}
        />
        <PaymentPlanLineItems lineItems={paymentPlanDetails.lineItems} />
        <View style={styles.sectionDivider} />
        <PaymentPlanAutopay
          sectionTitle={translatedText('paymentDetails')}
          isEnabled={isAutoPayEnabled}
          isDisabled={isSubmittingPlan}
          onChange={setIsAutoPayEnabled}
          isAccountLoading={isAccountLoading}
          account={paymentAccount}
          infoLink={{
            label: translatedText('whatIsAutopay'),
            onPress: handleWhatIsAutopayPress,
            analyticsId: 'paymentPlanWhatIsAutopayClicked',
          }}
        />
        <PaymentScheduleTimeline
          title={
            paymentPlanDetails.paymentSchedule.title ??
            translatedText('paymentSchedule')
          }
          items={paymentPlanDetails.paymentSchedule.scheduleItems.map(
            (item) => ({
              id: `${item.dueDate}-${item.amount.amount}`,
              label: item.label,
            }),
          )}
          disclosureText={
            paymentPlanDetails.paymentSchedule.disclosureText ||
            translatedText('lateFeeCaption')
          }
        />
        <View style={styles.sectionDivider} />
        <PaymentPlanAgreement
          isAgreed={isAgreed}
          onAgreementChange={() => setIsAgreed(!isAgreed)}
          checkboxAnalyticsId="paymentPlanTermsCheckbox"
          documentLink={{
            label: translatedText('truthInLendingAndAgreement'),
            onPress: viewTruthInLendingDocument,
            analyticsId: 'paymentPlanDisclosureClicked',
          }}
          notice={translatedText('acceptanceWarning')}
        />
      </PaymentPlanReviewLayout>

      <ScreenTransitionOverlay
        isLoading={isSubmittingPlan}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            bodyText={translatedText('loadingBody')}
          />
        }
        ErrorComponent={null}
      />
      <PaymentPlanWhatIsAutopayBottomSheet
        bottomSheetRef={whatIsAutopayBottomSheetRef}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: { backgroundColor: theme.color.background.primary, flex: 1 },
  sectionDivider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
});

export default PaymentPlan;
